import { useState } from "react";
import { FileText, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { btn } from "@/components/Page";

export function AboutBox({ about, canEdit, endpoint, onSaved, label = "About", placeholder = "Share a few words…", testid = "about", headerExtra = null, maxLength = 200, rows = 3, wrapperClassName = "mb-8 max-w-2xl" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(about || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { about: val.slice(0, maxLength) });
      onSaved?.(data.about);
      setEditing(false);
      toast.success("About updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!about && !canEdit) return null;

  return (
    <div className={wrapperClassName} data-testid={`${testid}-section`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2"><FileText size={15} /> {label}</h2>
        <div className="flex items-center gap-2">
          {headerExtra}
          {canEdit && !editing && (
            <button data-testid={`${testid}-edit-btn`} onClick={() => { setVal(about || ""); setEditing(true); }}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-400 border border-zinc-700 hover:border-amber-700/60 px-2.5 py-1 transition-colors">
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <div>
          <textarea data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)} maxLength={maxLength} rows={rows}
            placeholder={placeholder}
            className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none resize-none" />
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[11px] text-zinc-600 mr-auto">{val.length}/{maxLength}</span>
            <button data-testid={`${testid}-save-btn`} disabled={busy} onClick={save} className={btn() + " px-4 py-1.5"}><Check size={15} /></button>
            <button onClick={() => setEditing(false)} className={btn("ghost") + " px-4 py-1.5"}><X size={15} /></button>
          </div>
        </div>
      ) : about ? (
        <p data-testid={`${testid}-text`} className="text-sm text-zinc-300 whitespace-pre-wrap border border-zinc-800 bg-zinc-950/60 px-4 py-3 rounded-sm">{about}</p>
      ) : (
        <p className="text-sm text-zinc-600 italic border border-dashed border-zinc-800 px-4 py-3 rounded-sm">No description yet. Click Edit to add one.</p>
      )}
    </div>
  );
}
